/**
 * Delete Confirmation Dialog for Email Templates
 * Confirms template deletion with warning message
 *
 * @file src/components/email/templates/DeleteConfirmationDialog.tsx
 * @created 2025-10-28
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { EmailTemplate } from '@/api/email';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  template: EmailTemplate | null;
}

/**
 * DeleteConfirmationDialog Component
 *
 * Displays a confirmation dialog before deleting an email template.
 * Shows template name and usage count (if available).
 *
 * @example
 * <DeleteConfirmationDialog
 *   open={showDeleteDialog}
 *   onClose={() => setShowDeleteDialog(false)}
 *   onConfirm={handleConfirmDelete}
 *   template={templateToDelete}
 * />
 */
export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  template
}) => {
  if (!template) return null;

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Email Template
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              Are you sure you want to delete the template{' '}
              <strong className="text-foreground">"{template.template_name}"</strong>?
            </p>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone.
            </p>
            {template.usage_count !== undefined && template.usage_count > 0 && (
              <p className="text-sm text-amber-600 font-medium">
                ⚠️ This template has been used {template.usage_count} time{template.usage_count > 1 ? 's' : ''}.
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete Template
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteConfirmationDialog;
